import Vue from 'vue'
import { normalize } from 'normalizr'
import { S, G, M, A, YjCodeRelation } from './types'
import { Getters, Mutations, Actions, ExCommit } from 'vuex'
import { medicineSchema } from '@/schema/medicine'
import { MedicineEntities } from '@/schema/medicine/types'
import { Medicine } from '@/types/medicine'
import { MedicineListResponse } from '@/api/medicine/types'

/* -------------------------------------------------------------------------- */

const state = (): S => ({
  medicines: {},
  sort: [],
  yjCodeRelation: {},
  nextUrl: null,
  prevUrl: null,
  totalCount: 0
})

/* -------------------------------------------------------------------------- */

const getters: Getters<S, G> = {
  medicines: state => state.medicines,
  sort: state => state.sort,
  yjCodeRelation: state => state.yjCodeRelation,
  hasNext: state => state.nextUrl !== null,
  hasPrev: state => state.prevUrl !== null
}

/* -------------------------------------------------------------------------- */

const mutations: Mutations<S, M> = {
  setMedicines: (state, { medicines, sort }) => {
    state.medicines = medicines
    state.sort = sort
    state.yjCodeRelation = sort.reduce((relation, id) => {
      const { yjCode } = medicines[id]

      if (!Array.isArray(relation[yjCode])) {
        relation[yjCode] = []
      }

      relation[yjCode].push(id)

      return relation
    }, {} as YjCodeRelation)
  },

  addMedicines: (state, { medicines, sort, toAppend = true }) => {
    sort = sort.filter(id => state.medicines[id] === undefined)
    state.sort = toAppend ? state.sort.concat(sort) : sort.concat(state.sort)
    state.medicines = Object.assign({}, state.medicines, medicines)
  },

  setPagination: (state, { next, previous, count }) => {
    state.nextUrl = next
    state.prevUrl = previous
    state.totalCount = count
  }
}

/* -------------------------------------------------------------------------- */

export enum PaginateTypes {
  NEXT,
  PREV
}

const updateMedicines = (
  commit: ExCommit<M>,
  response: MedicineListResponse,
  direction: PaginateTypes
) => {
  const { result, entities } = normalize<
    Array<Medicine>,
    MedicineEntities,
    Array<number>
  >(response.results, [medicineSchema])

  const { medicines } = entities
  const { next, previous, count } = response

  commit('addMedicines', {
    medicines,
    sort: result,
    toAppend: direction === PaginateTypes.NEXT
  })
  commit('setPagination', { next, previous, count })
}

const actions: Actions<S, A, G, M> = {
  async fetch({ commit }, params) {
    const response = await Vue.$api.medicine.fetch(params)

    const { result, entities } = normalize<
      Array<Medicine>,
      MedicineEntities,
      Array<number>
    >(response.results, [medicineSchema])

    const { medicines } = entities
    const { next, previous, count } = response

    commit('setMedicines', { medicines, sort: result })
    commit('setPagination', { next, previous, count })
  },

  async paginate({ state, getters, commit }, { direction }) {
    let res: MedicineListResponse | null = null

    switch (direction) {
      case PaginateTypes.NEXT:
        if (!getters.hasNext) {
          return
        }
        res = await Vue.$api.medicine.fetchPage({
          url: (state.nextUrl as string).replace(/^https?:\/\/.*?api\//, '')
        })
        break

      case PaginateTypes.PREV:
        if (!getters.hasPrev) {
          return
        }
        res = await Vue.$api.medicine.fetchPage({
          url: (state.prevUrl as string).replace(/^https?:\/\/.*?api\//, '')
        })
        break

      default:
        throw new Error('direction is invalid')
    }

    updateMedicines(commit, res, direction)
  }
}

/* -------------------------------------------------------------------------- */

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
